import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchDeliveryCharges,
  createDeliveryCharge,
  updateDeliveryCharge,
  updateDeliveryChargeStatus,
  deleteDeliveryCharge,
} from '../services/deliveryChargeService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash,
} from 'lucide-react';

export default function DeliveryCharges() {
  const [charges, setCharges] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    minKilometer: '',
    maxKilometer: '',
    charge: '',
    isActive: '1',
  });
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  const loadCharges = async () => {
    try {
      const res = await fetchDeliveryCharges({ search, sortBy, sortOrder });
      const records = res.data?.data?.records || res.data?.data || [];
      setCharges(records);
    } catch (err) {
      toast.error('Failed to load delivery charges');
    }
  };

  useEffect(() => {
    loadCharges();
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (search === '') loadCharges();
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const openEdit = (ch) => {
    setEditingId(ch.id);
    setForm({
      minKilometer: String(ch.minKilometer),
      maxKilometer: ch.maxKilometer ? String(ch.maxKilometer) : '',
      charge: String(ch.charge),
      isActive: ch.isActive ? '1' : '0',
    });
    setOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm({ minKilometer: '', maxKilometer: '', charge: '', isActive: '1' });
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      minKilometer: Number(form.minKilometer),
      maxKilometer: form.maxKilometer ? Number(form.maxKilometer) : null,
      charge: Number(form.charge),
      isActive: form.isActive === '1',
    };
    try {
      if (editingId) {
        await updateDeliveryCharge(editingId, payload);
        toast.success('Delivery charge updated');
      } else {
        await createDeliveryCharge(payload);
        toast.success('Delivery charge created');
      }
      closeForm();
      loadCharges();
    } catch (err) {
      toast.error('Failed to save delivery charge');
    }
  };

  const toggleStatus = async (ch) => {
    const newStatus = !ch.isActive;
    const action = newStatus ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this delivery charge?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton: 'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateDeliveryChargeStatus(ch.id, newStatus);
      toast.success('Status updated');
      loadCharges();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const removeCharge = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure you want to delete this delivery charge?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton: 'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteDeliveryCharge(id);
      toast.success('Delivery charge deleted');
      loadCharges();
    } catch (err) {
      toast.error('Failed to delete delivery charge');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadCharges();
          }}
          className="relative"
        >
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-8 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>
        <button
          onClick={() => setOpen(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded"
        >
          Add Delivery Charge
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('minKilometer')}
              >
                <div className="flex items-center space-x-1">
                  <span>Min KM</span>
                  {sortBy === 'minKilometer' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('maxKilometer')}
              >
                <div className="flex items-center space-x-1">
                  <span>Max KM</span>
                  {sortBy === 'maxKilometer' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('charge')}
              >
                <div className="flex items-center space-x-1">
                  <span>Charge</span>
                  {sortBy === 'charge' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {charges.map((ch) => (
              <tr key={ch.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">{ch.minKilometer}</td>
                <td className="py-2 px-3">{ch.maxKilometer ?? '∞'}</td>
                <td className="py-2 px-3">{ch.charge}</td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${ch.isActive ? 'bg-green-500' : 'bg-red-500'}`}
                    onClick={() => toggleStatus(ch)}
                    title={`${ch.isActive ? 'Deactivate' : 'Activate'} delivery charge`}
                  >
                    {ch.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2">
                  <button
                    className="text-indigo-600"
                    onClick={() => openEdit(ch)}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => removeCharge(ch.id)}
                  >
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {charges.length === 0 && (
              <tr>
                <td className="py-2 px-3 text-center" colSpan="5">
                  <p className="text-sm text-gray-500 dark:text-gray-300">No delivery charges</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">{editingId ? 'Edit Delivery Charge' : 'Add Delivery Charge'}</h2>
              <button onClick={closeForm} className="text-gray-600 dark:text-gray-300">×</button>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <input
                name="minKilometer"
                value={form.minKilometer}
                onChange={(e) => setForm({ ...form, minKilometer: e.target.value })}
                placeholder="Min Kilometer"
                type="number"
                step="0.01"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                name="maxKilometer"
                value={form.maxKilometer}
                onChange={(e) => setForm({ ...form, maxKilometer: e.target.value })}
                placeholder="Max Kilometer"
                type="number"
                step="0.01"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                name="charge"
                value={form.charge}
                onChange={(e) => setForm({ ...form, charge: e.target.value })}
                placeholder="Charge"
                type="number"
                step="0.01"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <select
                name="isActive"
                value={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
              <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded">
                {editingId ? 'Update' : 'Create'} Delivery Charge
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

